<template>
  <v-container>
    <v-row>
      <v-col
        cols="12"
        class="d-flex justify-space-between"
      >
        <div>
          <h2>Abrechnungen</h2>
          <div>
            Hier finden Sie Ihre monatlichen Abgleichlisten und, wenn für Sie zutreffend, Rechnungen.
          </div>
        </div>
        <div>
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                v-on="on"
                v-bind="attrs"
                class="primary"
                @click="isSumListDialogOpen = true"
              >
                <v-icon>{{ mdiMicrosoftExcel }}</v-icon>
                Summenliste
              </v-btn>
            </template>
            <span>
              Summenliste erzeugen
            </span>
          </v-tooltip>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="flex-wrap">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Suche"
              hide-details
              single-line
              style="width: 400px"
              class="mr-6 flex-grow-0"
            ></v-text-field>
            <v-chip-group
              v-model="selectedTypes"
              multiple
              column
              color="primary"
              class="ml-auto"
            >
              <v-chip
                v-for="option in typeOptions"
                :key="option.value"
                :value="option.value"
                filter
                outlined
              >
                {{ option.text }}
              </v-chip>
            </v-chip-group>
          </v-card-title>
          <v-data-table
            :headers="headers"
            :items="filteredList"
            :search="search"
            :item-class="itemClasses"
            :custom-sort="customSort"
            sort-by="period_start"
            :sort-desc="true"
          >
            <template #item.period_start="{ item }">
              {{ momentize(item.period_start) }}
            </template>
            <template #item.name="{ item }">
              {{ item.name }}
            </template>

            <template #item.type="{ item }">
              {{ typeLabel(item.type) }}
            </template>

            <template #item.downloaded="{ item }">
              <v-tooltip
                v-if="item.first_download"
                bottom
              >
                <template #activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    v-on="on"
                    :color="archivedColor(item)"
                  >{{ mdiEmailOpenOutline }}
                  </v-icon>
                </template>
                <span>Heruntergeladen durch {{ item.first_download.user }}<br>am {{
                    simpleDate(item.first_download.date)
                  }}</span>
              </v-tooltip>
              <v-tooltip
                v-else
                bottom
              >
                <template #activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    v-on="on"
                    :color="archivedColor(item)"
                  >{{ mdiEmailOutline }}
                  </v-icon>
                </template>
                <span>Ungeöffnet</span>
              </v-tooltip>
            </template>

            <template #item.url="{ item }">
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    icon
                    v-bind="attrs"
                    v-on="on"
                    @click="downloadReport(item)"
                    :color="archivedColor(item)"
                  >
                    <v-icon>{{ mdiDownloadOutline }}</v-icon>
                  </v-btn>
                </template>
                <span>{{ item.name }} herunterladen</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
    <eval-invoicing-sum-list-dialog
      :is-open="isSumListDialogOpen"
      @close="isSumListDialogOpen = false"
    ></eval-invoicing-sum-list-dialog>
  </v-container>
</template>

<script>
import moment from "moment"
import { mdiEmailOutline, mdiEmailOpenOutline, mdiDownloadOutline, mdiMicrosoftExcel } from '@mdi/js'
import { mapGetters } from "vuex"
import { downloadBlob } from "@/lib/file-tools"
import EvalInvoicingSumListDialog from "@/components/evaluation/EvalInvoicingSumListDialog.vue"

export default {
  name: "EvalInvoicing",
  components: { EvalInvoicingSumListDialog },
  data () {
    return {
      mdiEmailOutline,
      mdiEmailOpenOutline,
      mdiDownloadOutline,
      mdiMicrosoftExcel,
      search: "",
      headers: [
        {
          text: "Zeitraum",
          value: "period_start",
        },
        {
          text: "Name",
          value: "name",
        },
        {
          text: "Typ",
          value: "type"
        },
        {
          text: "Status",
          value: "downloaded"
        },
        {
          text: "Download",
          value: "url"
        }
      ],
      bookingReports: [],
      invoices: [],
      camtExports: [],
      painExports: [],
      selectedTypes: [],
      isSumListDialogOpen: false,
    }
  },
  computed: {
    ...mapGetters("organization", { organization: "currentOrganization" }),
    ...mapGetters("user", ["isSuperuser"]),
    typeOptions: function () {
      const options = [
        { text: "Abgleichliste", value: "booking-report" },
        { text: "Rechnung", value: "invoice" }
      ]
      if (this.isSuperuser) {
        options.push(
          { text: "Kontoauszug (CAMT)", value: "camt" },
          { text: "Zahlungsauftrag (PAIN)", value: "pain" }
        )
      }
      return options
    },
    combinedList: function () {
      return [...this.bookingReports.map(x => ({
        ...x,
        type: "booking-report"
      })), ...this.invoices.map(x => ({
        ...x,
        type: "invoice"
      })), ...this.camtExports.map(x => ({
        ...x,
        type: "camt",
        name: `CAMT-Export ${x.account} #${x.sequence_number}`,
        period_start: x.created_at
      })), ...this.painExports.map(x => ({
        ...x,
        type: "pain",
        name: `PAIN-Export ${x.account}`,
        period_start: x.starts_at,
        period_end: x.ends_before
      }))]
    },
    filteredList: function () {
      if (!this.selectedTypes.length) return this.combinedList
      return this.combinedList.filter(item => this.selectedTypes.includes(item.type))
    }
  },
  created () {
    this.selectedTypes = this.typeOptions.map(o => o.value)
  },
  async mounted () {
    const requests = [
      this.$store.getters.restApi.get(`billing/export/booking_report?organization=${this.organization.id}`),
      this.$store.getters.restApi.get(`billing/invoice/?organization=${this.organization.id}`)
    ]
    if (this.isSuperuser) {
      requests.push(
        this.$store.getters.restApi.get(`billing/export/camt?organization=${this.organization.id}`),
        this.$store.getters.restApi.get(`billing/export/pain?organization=${this.organization.id}`)
      )
    }
    const results = await Promise.all(requests)
    this.bookingReports = results[0].data
    this.invoices = results[1].data
    if (this.isSuperuser) {
      this.camtExports = results[2].data
      this.painExports = results[3].data
    }
  },
  methods: {
    typeLabel (type) {
      return {
        "booking-report": "Abgleichliste",
        invoice: "Rechnung",
        camt: "Kontoauszug (CAMT)",
        pain: "Zahlungsauftrag (PAIN)"
      }[type] || type
    },
    customSort (items, sortByArr, sortDescArr) {
      if (!sortByArr.length) return items
      const [key] = sortByArr
      const desc = sortDescArr[0]
      const dateKeys = ["period_start", "period_end", "created_at"]
      return [...items].sort((a, b) => {
        let result
        if (dateKeys.includes(key)) {
          result = new Date(a[key]) - new Date(b[key])
        } else {
          result = (a[key] ?? "").toString().localeCompare((b[key] ?? "").toString())
        }
        return desc ? -result : result
      })
    },
    momentize (date) {
      return moment(date).format("MMMM YYYY")
    },
    simpleDate (date) {
      return moment(date).format('DD.MM.YYYY')
    },
    async downloadReport (report) {
      const response = await this.$store.getters.restApi.get(report.url + "download/", {
        responseType: 'blob',
      })
      const contentType = response.headers['content-type']
      const fileExtension = contentType === 'application/pdf' ? "pdf" : contentType === 'application/xml' ? "xml" : "xlsx"
      downloadBlob(response.data, `${report.name}.${fileExtension}`, contentType)
    },
    itemClasses (item) {
      return item?.archived ? 'archived' : ''
    },
    archivedColor (item) {
      return item?.archived ? 'grey lighten-1' : ''
    }
  }
}
</script>

<style lang="stylus">
.archived
  color: #BDBDBD
</style>
